package org.example.project.ui.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.ActivityStatus
import org.example.project.ui.components.EmptyStateView
import org.example.project.ui.components.FilterChip
import org.example.project.ui.components.LoadingStateView
import org.example.project.ui.components.PublishConfirmDialog
import org.example.project.ui.components.ReauthBanner
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.EditorColors
import org.example.project.ui.viewmodel.ActivityListUiState

@Composable
fun ActivityListScreen(
    state: ActivityListUiState,
    onSearchQueryChange: (String) -> Unit,
    onSearchSubmit: () -> Unit,
    onClearSearch: () -> Unit,
    onStatusFilterSelected: (ActivityStatus?) -> Unit,
    onPreviousPage: () -> Unit,
    onNextPage: () -> Unit,
    onAddNew: () -> Unit,
    onEdit: (ActivityResponse) -> Unit,
    onRequestPublish: (ActivityResponse) -> Unit,
    onDismissPublishDialog: () -> Unit,
    onConfirmPublish: () -> Unit,
    onReauthenticate: () -> Unit,
    modifier: Modifier = Modifier
) {
    val totalPages = state.totalPages
    val hasFilters = state.hasFilters
    val draftsOnPage = state.draftsOnPage
    val pubOnPage = state.pubOnPage

    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            .widthIn(max = 1080.dp)
    ) {
        val isCompact = maxWidth < 600.dp
        val isVeryNarrow = maxWidth < 460.dp

        Column(modifier = Modifier.fillMaxWidth()) {
            // CSRF Warning if needed
            if (state.isMutatingDisabled) {
                ReauthBanner(onReauthenticate = onReauthenticate)
            }

            // Summary metric cards (Dashboard block header)
            val filterLabel = if (hasFilters) "WYNIKI PO FILTRACH" else "POZYCJE W KATALOGU"
            val filterSubtext = if (hasFilters) "Dla zadanego wyszukiwania i filtrów" else "Wszystkie pozycje w katalogu"

            if (isVeryNarrow) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    MetricCard(
                        label = filterLabel,
                        value = "${state.totalCount}",
                        subtext = filterSubtext,
                        accentColor = EditorColors.Primary,
                        modifier = Modifier.fillMaxWidth()
                    )
                    MetricCard(
                        label = "BIEŻĄCA STRONA",
                        value = "${state.activities.size} / ${state.pageSize}",
                        subtext = "Strona ${state.currentPage + 1} z $totalPages",
                        accentColor = EditorColors.AccentTeal,
                        modifier = Modifier.fillMaxWidth()
                    )
                    MetricCard(
                        label = "SZKICE / OPUBLIKOWANE",
                        value = "$draftsOnPage / $pubOnPage",
                        subtext = "Pozycje na tej stronie",
                        accentColor = EditorColors.AccentLilac,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            } else if (isCompact) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    MetricCard(
                        label = filterLabel,
                        value = "${state.totalCount}",
                        subtext = filterSubtext,
                        accentColor = EditorColors.Primary,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        MetricCard(
                            label = "BIEŻĄCA STRONA",
                            value = "${state.activities.size} / ${state.pageSize}",
                            subtext = "Strona ${state.currentPage + 1} z $totalPages",
                            accentColor = EditorColors.AccentTeal,
                            modifier = Modifier.weight(1f)
                        )
                        MetricCard(
                            label = "SZKICE / OPUBLIKOWANE",
                            value = "$draftsOnPage / $pubOnPage",
                            subtext = "Na tej stronie",
                            accentColor = EditorColors.AccentLilac,
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            } else {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    MetricCard(
                        label = filterLabel,
                        value = "${state.totalCount}",
                        subtext = filterSubtext,
                        accentColor = EditorColors.Primary,
                        modifier = Modifier.weight(1f)
                    )
                    MetricCard(
                        label = "BIEŻĄCA STRONA",
                        value = "${state.activities.size} / ${state.pageSize}",
                        subtext = "Strona ${state.currentPage + 1} z $totalPages",
                        accentColor = EditorColors.AccentTeal,
                        modifier = Modifier.weight(1f)
                    )
                    MetricCard(
                        label = "SZKICE / OPUBLIKOWANE",
                        value = "$draftsOnPage / $pubOnPage",
                        subtext = "Pozycje na tej stronie",
                        accentColor = EditorColors.AccentLilac,
                        modifier = Modifier.weight(1f)
                    )
                }
            }

            // Action & Filter Toolbar Card
            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 16.dp),
                colors = CardDefaults.cardColors(containerColor = EditorColors.Surface),
                shape = RoundedCornerShape(16.dp),
                border = BorderStroke(1.dp, EditorColors.BorderSubtle),
                elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
            ) {
                Column(modifier = Modifier.padding(if (isCompact) 14.dp else 20.dp)) {
                    // Header row with title & CTA button
                    if (isCompact) {
                        Column(modifier = Modifier.fillMaxWidth()) {
                            Text(
                                text = "Katalog zabaw redakcji",
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = EditorColors.TextPrimary
                            )
                            Spacer(modifier = Modifier.height(2.dp))
                            Text(
                                text = "Przeglądaj, filtruj, edytuj i publikuj zabawy w katalogu",
                                fontSize = 13.sp,
                                color = EditorColors.TextSecondary
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                            Button(
                                onClick = onAddNew,
                                enabled = !state.isMutatingDisabled,
                                shape = RoundedCornerShape(10.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = EditorColors.Primary,
                                    contentColor = EditorColors.Surface
                                ),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(42.dp)
                            ) {
                                Icon(
                                    imageVector = AppIcons.Add,
                                    contentDescription = null,
                                    modifier = Modifier.size(16.dp)
                                )
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(
                                    text = "Dodaj nową zabawę",
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column {
                                Text(
                                    text = "Katalog zabaw redakcji",
                                    fontSize = 18.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = EditorColors.TextPrimary
                                )
                                Spacer(modifier = Modifier.height(2.dp))
                                Text(
                                    text = "Przeglądaj, filtruj, edytuj i publikuj zabawy w katalogu",
                                    fontSize = 13.sp,
                                    color = EditorColors.TextSecondary
                                )
                            }

                            Button(
                                onClick = onAddNew,
                                enabled = !state.isMutatingDisabled,
                                shape = RoundedCornerShape(10.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = EditorColors.Primary,
                                    contentColor = EditorColors.Surface
                                ),
                                modifier = Modifier.height(42.dp)
                            ) {
                                Icon(
                                    imageVector = AppIcons.Add,
                                    contentDescription = null,
                                    modifier = Modifier.size(16.dp)
                                )
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(
                                    text = "Dodaj nową zabawę",
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    // Search field row
                    if (isCompact) {
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            OutlinedTextField(
                                value = state.searchQuery,
                                onValueChange = onSearchQueryChange,
                                placeholder = { Text("Wyszukaj po nazwie lub opisie...", fontSize = 13.sp) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp),
                                leadingIcon = {
                                    Icon(
                                        imageVector = AppIcons.Search,
                                        contentDescription = null,
                                        tint = EditorColors.TextSecondary,
                                        modifier = Modifier.size(18.dp)
                                    )
                                },
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = EditorColors.Primary,
                                    unfocusedBorderColor = EditorColors.Border
                                )
                            )

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Button(
                                    onClick = onSearchSubmit,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = EditorColors.Primary,
                                        contentColor = EditorColors.Surface
                                    ),
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(40.dp)
                                ) {
                                    Text("Szukaj", fontSize = 13.sp, fontWeight = FontWeight.Medium)
                                }

                                if (state.searchQuery.isNotBlank()) {
                                    OutlinedButton(
                                        onClick = onClearSearch,
                                        shape = RoundedCornerShape(10.dp),
                                        border = BorderStroke(1.dp, EditorColors.BorderStrong),
                                        modifier = Modifier
                                            .weight(1f)
                                            .height(40.dp)
                                    ) {
                                        Text("Wyczyść", fontSize = 13.sp, color = EditorColors.TextSecondary)
                                    }
                                }
                            }
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(10.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            OutlinedTextField(
                                value = state.searchQuery,
                                onValueChange = onSearchQueryChange,
                                placeholder = { Text("Wyszukaj po nazwie lub opisie...", fontSize = 13.sp) },
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp),
                                leadingIcon = {
                                    Icon(
                                        imageVector = AppIcons.Search,
                                        contentDescription = null,
                                        tint = EditorColors.TextSecondary,
                                        modifier = Modifier.size(18.dp)
                                    )
                                },
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = EditorColors.Primary,
                                    unfocusedBorderColor = EditorColors.Border
                                )
                            )

                            Button(
                                onClick = onSearchSubmit,
                                shape = RoundedCornerShape(10.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = EditorColors.Primary,
                                    contentColor = EditorColors.Surface
                                ),
                                modifier = Modifier.height(44.dp)
                            ) {
                                Text("Szukaj", fontSize = 13.sp, fontWeight = FontWeight.Medium)
                            }

                            if (state.searchQuery.isNotBlank()) {
                                OutlinedButton(
                                    onClick = onClearSearch,
                                    shape = RoundedCornerShape(10.dp),
                                    border = BorderStroke(1.dp, EditorColors.BorderStrong),
                                    modifier = Modifier.height(44.dp)
                                ) {
                                    Text("Wyczyść", fontSize = 13.sp, color = EditorColors.TextSecondary)
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(14.dp))

                    // Status Filter Chips
                    @OptIn(ExperimentalLayoutApi::class)
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = "Status:",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = EditorColors.TextMuted,
                            modifier = Modifier.padding(end = 4.dp, top = 6.dp)
                        )

                        FilterChip(
                            label = "Wszystkie",
                            selected = state.selectedStatusFilter == null,
                            onClick = { onStatusFilterSelected(null) }
                        )
                        FilterChip(
                            label = "Tylko szkice (DRAFT)",
                            selected = state.selectedStatusFilter == ActivityStatus.DRAFT,
                            onClick = { onStatusFilterSelected(ActivityStatus.DRAFT) }
                        )
                        FilterChip(
                            label = "Opublikowane (PUBLISHED)",
                            selected = state.selectedStatusFilter == ActivityStatus.PUBLISHED,
                            onClick = { onStatusFilterSelected(ActivityStatus.PUBLISHED) }
                        )
                    }
                }
            }

            // List Content or Loading / Empty state
            if (state.isLoading) {
                LoadingStateView(message = "Wczytywanie listy zabaw...")
            } else if (state.activities.isEmpty()) {
                EmptyStateView(
                    title = "Brak zabaw spełniających wybrane kryteria",
                    description = if (hasFilters) {
                        "Nie znaleziono pozycji pasujących do zadanego zapytania lub filtrów. Spróbuj wyczyścić filtry."
                    } else {
                        "Katalog redakcyjny jest obecnie pusty. Możesz dodać pierwszą zabawę klikając poniższy przycisk."
                    },
                    actionLabel = if (hasFilters) "Wyczyść filtry" else "+ Dodaj nową zabawę",
                    onAction = {
                        if (hasFilters) {
                            onClearSearch()
                        } else {
                            onAddNew()
                        }
                    }
                )
            } else {
                LazyColumn(
                    modifier = Modifier.weight(1f, fill = false),
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    items(state.activities) { activity ->
                        ActivityCardItem(
                            activity = activity,
                            isMutatingDisabled = state.isMutatingDisabled,
                            onEdit = { onEdit(activity) },
                            onPublish = { onRequestPublish(activity) }
                        )
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                // Pagination Controls Card
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = EditorColors.Surface),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, EditorColors.BorderSubtle)
                ) {
                    if (isCompact) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(12.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Text(
                                text = "Strona ${state.currentPage + 1} z $totalPages (wyników: ${state.totalCount})",
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Medium,
                                color = EditorColors.TextSecondary
                            )
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                OutlinedButton(
                                    onClick = onPreviousPage,
                                    enabled = state.currentPage > 0,
                                    shape = RoundedCornerShape(8.dp),
                                    border = BorderStroke(1.dp, EditorColors.BorderStrong),
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Text(
                                        text = "« Poprzednia",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Medium,
                                        color = if (state.currentPage > 0) EditorColors.TextPrimary else EditorColors.TextDisabled
                                    )
                                }
                                OutlinedButton(
                                    onClick = onNextPage,
                                    enabled = state.currentPage < totalPages - 1,
                                    shape = RoundedCornerShape(8.dp),
                                    border = BorderStroke(1.dp, EditorColors.BorderStrong),
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Text(
                                        text = "Następna »",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Medium,
                                        color = if (state.currentPage < totalPages - 1) EditorColors.TextPrimary else EditorColors.TextDisabled
                                    )
                                }
                            }
                        }
                    } else {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp, vertical = 10.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            OutlinedButton(
                                onClick = onPreviousPage,
                                enabled = state.currentPage > 0,
                                shape = RoundedCornerShape(8.dp),
                                border = BorderStroke(1.dp, EditorColors.BorderStrong)
                            ) {
                                Text(
                                    text = "« Poprzednia",
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = if (state.currentPage > 0) EditorColors.TextPrimary else EditorColors.TextDisabled
                                )
                            }

                            Text(
                                text = "Strona ${state.currentPage + 1} z $totalPages (wyników: ${state.totalCount})",
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Medium,
                                color = EditorColors.TextSecondary
                            )

                            OutlinedButton(
                                onClick = onNextPage,
                                enabled = state.currentPage < totalPages - 1,
                                shape = RoundedCornerShape(8.dp),
                                border = BorderStroke(1.dp, EditorColors.BorderStrong)
                            ) {
                                Text(
                                    text = "Następna »",
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = if (state.currentPage < totalPages - 1) EditorColors.TextPrimary else EditorColors.TextDisabled
                                )
                            }
                        }
                    }
                }
            }

            // Publish confirmation dialog
            state.publishConfirmActivity?.let { activity ->
                PublishConfirmDialog(
                    activity = activity,
                    onDismiss = onDismissPublishDialog,
                    onConfirm = onConfirmPublish
                )
            }
        }
    }
}

@Composable
private fun MetricCard(
    label: String,
    value: String,
    subtext: String,
    accentColor: Color,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = EditorColors.Surface),
        shape = RoundedCornerShape(14.dp),
        border = BorderStroke(1.dp, EditorColors.BorderSubtle),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(
                text = label,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                color = EditorColors.TextMuted,
                letterSpacing = 0.5.sp
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = value,
                fontSize = 20.sp,
                fontWeight = FontWeight.ExtraBold,
                color = accentColor
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = subtext,
                fontSize = 11.sp,
                color = EditorColors.TextSecondary
            )
        }
    }
}
